import type { Node, Edge } from '@xyflow/react';
import type { TreeNodeDef, TreeLayoutConfig } from './types';

const DEFAULT_CONFIG: TreeLayoutConfig = { hGap: 40, vGap: 60 };

function computeSubtreeWidth(
  nodeId: string,
  widthMap: Map<string, number>,
  childrenMap: Map<string, string[]>,
  hGap: number,
): number {
  const kids = childrenMap.get(nodeId) ?? [];
  const nodeWidth = widthMap.get(nodeId) ?? 200;
  if (kids.length === 0) return nodeWidth;
  const childrenTotal =
    kids.reduce((sum, kid) => sum + computeSubtreeWidth(kid, widthMap, childrenMap, hGap), 0) +
    hGap * (kids.length - 1);
  return Math.max(nodeWidth, childrenTotal);
}

function placeNodes(
  nodeId: string,
  x: number,
  y: number,
  widthMap: Map<string, number>,
  heightMap: Map<string, number>,
  childrenMap: Map<string, string[]>,
  config: TreeLayoutConfig,
  positions: Map<string, { x: number; y: number }>,
): void {
  positions.set(nodeId, { x, y });
  const kids = childrenMap.get(nodeId) ?? [];
  if (kids.length === 0) return;

  const nodeWidth = widthMap.get(nodeId) ?? 200;
  const nodeHeight = heightMap.get(nodeId) ?? 80;
  const subtreeWidths = kids.map((kid) =>
    computeSubtreeWidth(kid, widthMap, childrenMap, config.hGap),
  );
  const totalChildWidth =
    subtreeWidths.reduce((sum, w) => sum + w, 0) + config.hGap * (kids.length - 1);

  let childX = x + nodeWidth / 2 - totalChildWidth / 2;
  const childY = y + nodeHeight + config.vGap;

  for (let i = 0; i < kids.length; i++) {
    const kidWidth = subtreeWidths[i];
    const kidNodeWidth = widthMap.get(kids[i]) ?? 200;
    placeNodes(
      kids[i],
      childX + kidWidth / 2 - kidNodeWidth / 2,
      childY,
      widthMap,
      heightMap,
      childrenMap,
      config,
      positions,
    );
    childX += kidWidth + config.hGap;
  }
}

function getHiddenDescendants(
  collapsedIds: Set<string>,
  childrenMap: Map<string, string[]>,
): Set<string> {
  const hidden = new Set<string>();
  const queue = [...collapsedIds];
  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const kid of childrenMap.get(id) ?? []) {
      if (!hidden.has(kid)) {
        hidden.add(kid);
        queue.push(kid);
      }
    }
  }
  return hidden;
}

export function computeTreeLayout(
  nodeDefs: TreeNodeDef[],
  collapsedIds: Set<string>,
  config: TreeLayoutConfig = DEFAULT_CONFIG,
): { nodes: Node[]; edges: Edge[] } {
  if (nodeDefs.length === 0) return { nodes: [], edges: [] };

  const nodeIds = new Set(nodeDefs.map((n) => n.id));

  const fullChildrenMap = new Map<string, string[]>(nodeDefs.map((n) => [n.id, []]));
  for (const node of nodeDefs) {
    if (node.parentId !== null && nodeIds.has(node.parentId)) {
      fullChildrenMap.get(node.parentId)!.push(node.id);
    }
  }

  const hiddenIds = getHiddenDescendants(collapsedIds, fullChildrenMap);
  const visibleNodes = nodeDefs.filter((n) => !hiddenIds.has(n.id));
  const visibleIds = new Set(visibleNodes.map((n) => n.id));

  const visibleChildrenMap = new Map<string, string[]>(visibleNodes.map((n) => [n.id, []]));
  for (const node of visibleNodes) {
    if (node.parentId !== null && visibleIds.has(node.parentId)) {
      visibleChildrenMap.get(node.parentId)!.push(node.id);
    }
  }

  const widthMap = new Map(visibleNodes.map((n) => [n.id, n.width]));
  const heightMap = new Map(visibleNodes.map((n) => [n.id, n.height]));

  const root = visibleNodes.find((n) => n.parentId === null || !visibleIds.has(n.parentId));
  if (!root) return { nodes: [], edges: [] };

  const positions = new Map<string, { x: number; y: number }>();
  placeNodes(root.id, 0, 0, widthMap, heightMap, visibleChildrenMap, config, positions);

  const nodes: Node[] = visibleNodes.map((n) => ({
    id: n.id,
    type: n.type,
    position: positions.get(n.id) ?? { x: 0, y: 0 },
    width: n.width,
    height: n.height,
    measured: { width: n.width, height: n.height },
    data: n.data,
  }));

  const nodeDefMap = new Map(nodeDefs.map((n) => [n.id, n]));

  const edges: Edge[] = visibleNodes
    .filter((n) => n.parentId !== null && visibleIds.has(n.parentId))
    .map((n) => {
      const parent = nodeDefMap.get(n.parentId!);
      return {
        id: `e-${n.parentId}-${n.id}`,
        source: n.parentId!,
        target: n.id,
        style: parent?.edgeStyle,
      };
    });

  return { nodes, edges };
}
